import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";

import {
  Box,
  Button,
  Paper,
  Stack,
  Alert,
  Container,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import StampIcon from "@mui/icons-material/Fingerprint";
import EmailIcon from "@mui/icons-material/Email"; // Changed from WhatsAppIcon

import { Document, Page } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import config from "../config.js";
import { useSelector } from "react-redux";

const DocumentSigner = () => {
  const { token } = useSelector((state) => state.auth);
  const { API_BASE_URL } = config;
  const [currentStep, setCurrentStep] = useState(0);
  const [document, setDocument] = useState(null);
  const [signature, setSignature] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [error, setError] = useState("");
  const [selectedPage] = useState(1);
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [otp, setOtp] = useState("");
  
  // ==================== EMAIL IMPLEMENTATION ====================
  const [email, setEmail] = useState(""); // Changed from whatsappNumber
  // const [whatsappNumber, setWhatsappNumber] = useState(""); // For future MSG91 use
  // ==================== END EMAIL VARIABLES ====================
  
  const [digitalStamps, setDigitalStamps] = useState([]);
  const [numPages, setNumPages] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedStoredSignature, setSelectedStoredSignature] = useState(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const canvasRef = useRef(null);
  const pdfContainerRef = useRef(null);

  const steps = [
    "Upload Document",
    "Add Digital Stamps",
    "Enter Sign Details",
    "Place Signature",
    "Review & Send",
  ];

  useEffect(() => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
    }
  }, []);

  const fetchSignatures = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/getUserSignatures`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const signaturesData = response.data.data?.signatures || [];
      
        setSignatures(signaturesData);
      } else {
        throw new Error(response.data.message || "Failed to fetch signatures");
      }
    } catch (error) {
      console.error("Error fetching signatures:", error);
      setError(error.response?.data?.message || "Failed to load signatures");
      setSignatures([]);
    } finally {
      setLoading(false);
    }
  }, [token, API_BASE_URL]);

  useEffect(() => {
    if (currentStep === 3) {
      fetchSignatures();
    }
  }, [currentStep, fetchSignatures]);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/pdf") {
      setDocument(file);
      setCurrentStep(1);
    } else {
      setError("Please upload a valid PDF document");
    }
  };

  const generateUniqueSerialNumber = (pageNumber) => {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substr(2, 4);
    const pageStr = String(pageNumber).padStart(3, "0");
    return `DS${timestamp}${randomPart}${pageStr}`.toUpperCase();
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    const stamps = Array.from({ length: numPages }, (_, index) => ({
      pageNumber: index + 1,
      serialNumber: generateUniqueSerialNumber(index + 1),
      affixed: true,
      position: {
        x: 350,
        y: 700,
      },
    }));
    setDigitalStamps(stamps);
  };

  const verifySerialNumberUniqueness = (stamps) => {
    if (!stamps || !Array.isArray(stamps)) {
      return false;
    }
    const serialNumbers = stamps.map((stamp) => stamp.serialNumber);
    return new Set(serialNumbers).size === serialNumbers.length;
  };

  const getCoordinates = (e) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startDrawing = (e) => {
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      setSignature(canvasRef.current.toDataURL());
    }
  };

  const clearSignature = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setSignature(null);
    setError("");
  };

  // ==================== EMAIL IMPLEMENTATION ====================
  const sendOTP = async () => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      setOtpLoading(true);
      setError("");
      
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/send-whatsapp-otp`, // Keep endpoint name for compatibility
        {
          email: email,
          // whatsappNumber: whatsappNumber, // Optional for future use
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setShowOtpDialog(true);
        setError("");
      } else {
        setError(response.data.message || "Failed to send OTP");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp) {
      setError("Please enter the OTP");
      return;
    }

    if (otp.length !== 4) {
      setError("OTP must be 4 digits");
      return;
    }

    try {
      setVerifyLoading(true);
      setError("");
      
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/verify-whatsapp-otp`, // Keep endpoint name for compatibility
        {
          email: email,
          otp: otp,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setShowOtpDialog(false);
        setCurrentStep(3);
        setError("");
        setOtp("");
      } else {
        setError(response.data.message || "Invalid OTP");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to verify OTP");
    } finally {
      setVerifyLoading(false);
    }
  };
  // ==================== END EMAIL IMPLEMENTATION ====================

  // ==================== MSG91 WHATSAPP (COMMENTED FOR FUTURE USE) ====================
  /*
  const sendOTP = async () => {
    if (!whatsappNumber) {
      setError("Please enter your WhatsApp number");
      return;
    }

    try {
      setOtpLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/send-whatsapp-otp`,
        {
          whatsappNumber: whatsappNumber,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setShowOtpDialog(true);
        setError("");
      } else {
        setError(response.data.message || "Failed to send OTP");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp) {
      setError("Please enter the OTP");
      return;
    }

    try {
      setVerifyLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/verify-whatsapp-otp`,
        {
          whatsappNumber: whatsappNumber,
          otp: otp,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setShowOtpDialog(false);
        setCurrentStep(3);
        setError("");
      } else {
        setError(response.data.message || "Invalid OTP");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to verify OTP");
    } finally {
      setVerifyLoading(false);
    }
  };
  */
  // ==================== END MSG91 WHATSAPP ====================

  const getSignatureBytes = async (signatureDataUrl) => {
    try {
      if (!signatureDataUrl) return null;

      if (signatureDataUrl.startsWith("data:")) {
        const base64Data = signatureDataUrl.split(",")[1];
        const binaryString = window.atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
      } else {
        const response = await fetch(signatureDataUrl);
        const blob = await response.blob();
        return await blob.arrayBuffer();
      }
    } catch (error) {
      console.error("Error processing signature:", error);
      throw new Error("Failed to process signature");
    }
  };

  const handleSignatureSelect = async (signature) => {
    try {
      setLoading(true);
      setError("");
      setSelectedStoredSignature(signature);

      if (!signature.id) {
        throw new Error("Invalid signature selection");
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/v1/get-signature-by-id/${signature.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      const objectUrl = URL.createObjectURL(response.data);
      setSignature(objectUrl);
    } catch (error) {
      console.error("Error in handleSignatureSelect:", error);
      setError(`Failed to load signature: ${error.message}`);
      setSelectedStoredSignature(null);
      setSignature(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (signature && signature.startsWith("blob:")) {
        URL.revokeObjectURL(signature);
      }
    };
  }, [signature]);

  const handleSubmit = async () => {
    try {
      // Changed validation from whatsappNumber to email
      if (!document || !signature || !email) {
        throw new Error("Please complete all required fields");
      }

      if (!digitalStamps || !Array.isArray(digitalStamps)) {
        throw new Error("Digital stamps not properly initialized");
      }

      if (!verifySerialNumberUniqueness(digitalStamps)) {
        throw new Error("Duplicate serial numbers detected. Please try again.");
      }

      setLoading(true);

      const pdfBytes = await document.arrayBuffer();
      const { PDFDocument, rgb } = await import("pdf-lib");
      const pdfDoc = await PDFDocument.load(pdfBytes);

      let signatureBytes;
      try {
        signatureBytes = await getSignatureBytes(
          signature,
          selectedStoredSignature
        );
        if (!signatureBytes) {
          throw new Error("Failed to process signature");
        }
      } catch (error) {
        console.error("Signature processing error:", error);
        throw new Error("Failed to process signature. Please try again.");
      }

      const signatureImage = await pdfDoc.embedPng(signatureBytes);

      for (const stamp of digitalStamps) {
        if (!stamp.affixed) continue;

        const page = pdfDoc.getPage(stamp.pageNumber - 1);
        const { height } = page.getSize();

        page.drawImage(signatureImage, {
          x: stamp.position.x,
          y: height - stamp.position.y,
          width: 200,
          height: 100,
        });

        const stampText = `Digital Stamp: ${stamp.serialNumber}\nSigned: ${new Date().toISOString()}`;
        page.drawText(stampText, {
          x: stamp.position.x,
          y: height - stamp.position.y - 120,
          size: 10,
          color: rgb(0, 0, 0.5),
        });
      }

      const modifiedPdfBytes = await pdfDoc.save();
      const signedPdfBlob = new Blob([modifiedPdfBytes], {
        type: "application/pdf",
      });

      const formData = new FormData();
      formData.append(
        "signedDocument",
        new File([signedPdfBlob], "signed_document.pdf", {
          type: "application/pdf",
        })
      );
      // Changed from whatsappNumber to email
      formData.append("email", email);
      // formData.append("whatsappNumber", whatsappNumber); // For future MSG91 use
      formData.append("digitalStamps", JSON.stringify(digitalStamps));

      const response = await axios.post(
        `${API_BASE_URL}/api/v1/submit-signed-document`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            
          },
        }
      );

      if (response.data.success) {
        setError("");
        setCurrentStep(0);
        setDocument(null);
        setSignature(null);
        setEmail(""); // Changed from setWhatsappnumber
        setDigitalStamps([]);

        // Changed success message
        alert("Document successfully signed and sent via email!");
      } else {
        throw new Error(response.data.message || "Failed to process document");
      }
    } catch (error) {
      console.error("Error in document submission:", error);
      setError(error.message || "Failed to process document");
    } finally {
      setLoading(false);
    }
  };

  const renderSignatureOptions = () => (
    <Box sx={{ width: "100%", mb: 4 }}>
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
      >
        <Tab label="Draw Signature" />
        <Tab label="Saved Signatures" />
      </Tabs>

      <Box sx={{ mt: 2 }}>
        {activeTab === 0 ? (
          <Box>
            <canvas
              ref={canvasRef}
              width={400}
              height={200}
              style={{
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "crosshair",
                touchAction: "none",
              }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={(e) => {
                e.preventDefault();
                startDrawing(e.touches[0]);
              }}
              onTouchMove={(e) => {
                e.preventDefault();
                draw(e.touches[0]);
              }}
              onTouchEnd={stopDrawing}
            />
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                onClick={clearSignature}
              >
                Clear
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            {loading ? (
              <CircularProgress />
            ) : (
              <Grid container spacing={2}>
                {signatures && signatures.length > 0 ? (
                  signatures.map((sig) => (
                    <Grid item xs={6} sm={4} key={sig.id}>
                      <Card
                        sx={{
                          cursor: "pointer",
                          border:
                            selectedStoredSignature?.id === sig.id ? 2 : 0,
                          borderColor: "primary.main",
                        }}
                        onClick={() => handleSignatureSelect(sig)}
                      >
                        <CardContent>
                          <img
                            src={sig.previewUrl}
                            alt={`Signature`}
                            style={{ width: "100%", height: "auto" }}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Typography>No saved signatures found.</Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderPageContent = (pageNumber) => {
    const currentPageStamp = digitalStamps && digitalStamps[pageNumber - 1];

    return (
      <Box key={`page_${pageNumber}`} sx={{ mb: 2, position: "relative" }}>
        <Page
          pageNumber={pageNumber}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
        {currentPageStamp && (
          <Box
            sx={{
              position: "absolute",
              bottom: "20px",
              right: "20px",
              backgroundColor: "rgba(0, 0, 255, 0.1)",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid blue",
              zIndex: 1,
            }}
          >
            Serial: {currentPageStamp.serialNumber}
          </Box>
        )}
        {signature && selectedPage === pageNumber && (
          <img
            src={signature}
            alt="Signature"
            style={{
              position: "absolute",
              right: "20px",
              bottom: "80px",
              width: "200px",
              pointerEvents: "none",
            }}
          />
        )}
        <Tooltip title="Digital stamp">
          <StampIcon
            sx={{
              position: "absolute",
              bottom: "25px",
              right: "180px",
              color: "primary.main",
            }}
          />
        </Tooltip>
      </Box>
    );
  };

  const getDocumentPreview = () => (
    <Box ref={pdfContainerRef} sx={{ mb: 2 }}>
      {document && (
        <Document file={document} onLoadSuccess={onDocumentLoadSuccess}>
          {numPages &&
            Array.from(new Array(numPages), (_, index) =>
              renderPageContent(index + 1)
            )}
        </Document>
      )}
    </Box>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <input
              type="file"
              accept=".pdf"
              style={{ display: "none" }}
              id="document-upload"
              onChange={handleFileUpload}
            />
            <label htmlFor="document-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadFileIcon />}
                sx={{ width: 200 }}
              >
                Upload PDF
              </Button>
            </label>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Digital Stamps Placement Preview
            </Typography>
            {getDocumentPreview()}
            <Button variant="contained" onClick={() => setCurrentStep(2)}>
              Continue
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Enter Sign Details
            </Typography>
            <Stack spacing={2}>
              {/* Changed from WhatsApp to Email */}
              <TextField
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                fullWidth
                placeholder="your.email@example.com"
                error={Boolean(error && error.includes("email"))}
                helperText={error && error.includes("email") ? error : ""}
              />
              <Button
                variant="contained"
                startIcon={<EmailIcon />}
                onClick={sendOTP}
                disabled={!email || otpLoading}
              >
                {otpLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Send OTP"
                )}
              </Button>
            </Stack>

            <Dialog
              open={showOtpDialog}
              onClose={() => setShowOtpDialog(false)}
            >
              <DialogTitle>Enter OTP</DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  We've sent a 4-digit OTP to {email}
                </Typography>
                <TextField
                  autoFocus
                  margin="dense"
                  label="OTP"
                  type="text"
                  fullWidth
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 4);
                    setOtp(value);
                  }}
                  error={Boolean(error && error.includes("OTP"))}
                  helperText={error && error.includes("OTP") ? error : ""}
                  inputProps={{
                    maxLength: 4,
                    pattern: "[0-9]*",
                  }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowOtpDialog(false)}>Cancel</Button>
                <Button
                  onClick={verifyOTP}
                  variant="contained"
                  disabled={verifyLoading || otp.length !== 4}
                >
                  {verifyLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Verify OTP"
                  )}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Choose or Draw Signature
            </Typography>
            {renderSignatureOptions()}
            {getDocumentPreview()}
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() =>
                (signature || selectedStoredSignature) && setCurrentStep(4)
              }
              disabled={!signature && !selectedStoredSignature}
              sx={{ mt: 2 }}
            >
              Continue
            </Button>
          </Box>
        );

      case 4:
        return (
          <Box sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Review and Send Document
            </Typography>

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography>Document: {document?.name}</Typography>
                {/* Changed from WhatsApp to Email */}
                <Typography>Email: {email}</Typography>
                <Typography>
                  Digital Stamps:{" "}
                  {digitalStamps &&
                    digitalStamps.filter((s) => s.affixed).length}
                </Typography>
                <Typography>
                  Signature Position: Page {selectedPage}
                </Typography>
              </CardContent>
            </Card>

            <Typography variant="h6" sx={{ mb: 2 }}>
              Document Preview
            </Typography>

            <Box
              sx={{
                maxHeight: "600px",
                overflow: "auto",
                mb: 3,
                border: "1px solid #ddd",
                borderRadius: 1,
              }}
            >
              {document && (
                <Document
                  file={document}
                  onLoadSuccess={onDocumentLoadSuccess}
                >
                  {numPages &&
                    Array.from(new Array(numPages), (_, index) =>
                      renderPageContent(index + 1)
                    )}
                </Document>
              )}
            </Box>

            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSubmit}
              sx={{ mt: 2 }}
            >
              Send Document
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={2} sx={{ p: 3 }}>
        <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default DocumentSigner;
